import { affectsWorkspace, changedOrderIds, latestRequest } from '@/services/workspaceRefresh'
import { useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { AdminPageHero } from '@/components/common/AdminPageHero'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { orderDetailsText, StoredOrderDialog } from '@/components/forms/StoredOrderView'
import { useAuth } from '@/context/AuthContext'
import { useCrmHub } from '@/context/SignalRContext'
import { readError } from '@/services/apiClient'
import { loadOpenOrders, markOrderProcessed, type CustomerOrder } from '@/services/customerService'
import { loadStoredOrder } from '@/services/formService'

type OrderSortColumn = 'placedAt' | 'customerName' | 'formName' | 'status'
const orderStatusLabel = (order: CustomerOrder) => order.status === 'Processing' ? 'Processing' : 'Waiting'
const orderSortLabel = (order: CustomerOrder, column: Exclude<OrderSortColumn, 'placedAt'>) =>
  column === 'status' ? orderStatusLabel(order) : column === 'formName' ? order.formName || 'Submission' : order.customerName || 'Customer'

export function OrdersPage() {
  const { isDemo } = useAuth()
  const { lastEvent, replaceOpenOrderCount } = useCrmHub()
  const [orders, setOrders] = useState<CustomerOrder[]>([])
  const [loading, setLoading] = useState(!isDemo)
  const [error, setError] = useState<string | null>(null)
  const [viewId, setViewId] = useState<number | null>(null)
  const [confirmId, setConfirmId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [sort, setSort] = useState<{ column: OrderSortColumn; ascending: boolean }>({ column: 'placedAt', ascending: true })

  function sortBy(column: OrderSortColumn) {
    setSort(current => ({ column, ascending: current.column === column ? !current.ascending : true }))
  }

  const sortedOrders = [...orders].sort((a, b) => {
    const comparison = sort.column === 'placedAt'
      ? new Date(a.placedAt).getTime() - new Date(b.placedAt).getTime()
      : orderSortLabel(a, sort.column).localeCompare(orderSortLabel(b, sort.column), undefined, { sensitivity: 'base', numeric: true })
    return (sort.ascending ? comparison : -comparison)
      || new Date(a.placedAt).getTime() - new Date(b.placedAt).getTime()
      || a.id - b.id
  })

  function sortHeader(column: OrderSortColumn, label: string) {
    const active = sort.column === column
    return (
      <th aria-sort={active ? (sort.ascending ? 'ascending' : 'descending') : 'none'}>
        <button type="button" className="btn btn-link p-0 text-reset text-decoration-none fw-bold"
          onClick={() => sortBy(column)}
          aria-label={`Sort by ${label}, ${active && sort.ascending ? 'descending' : 'ascending'}`}>
          {label} <span aria-hidden="true">{active ? (sort.ascending ? '↑' : '↓') : '↕'}</span>
        </button>
      </th>
    )
  }

  const request = useRef(latestRequest())
  const pendingOrderIds = useRef(new Set<number>())
  const refreshingAll = useRef(false)
  const [confirmVersion, setConfirmVersion] = useState<string | undefined>()
  useEffect(() => () => { request.current() }, [])

  function confirmOrder(id: number) {
    setError(null)
    setConfirmVersion(orders.find(order => order.id === id)?.dateModified)
    setConfirmId(id)
  }

  function refresh() {
    if (isDemo) return Promise.resolve()
    const current = request.current()
    refreshingAll.current = true
    pendingOrderIds.current.clear()
    return loadOpenOrders()
      .then((rows) => {
        if (!current()) return
        setOrders(rows)
        replaceOpenOrderCount(rows.length)
      })
      .catch((caught) => { if (current()) setError(readError(caught)) })
      .finally(() => { if (current()) { refreshingAll.current = false; setLoading(false) } })
  }

  useEffect(() => {
    void refresh()
  }, [isDemo])

  useEffect(() => {
    if (isDemo || !affectsWorkspace(lastEvent, 'orders')) return
    const ids = changedOrderIds(lastEvent)
    if (!ids || loading || refreshingAll.current) { void refresh(); return }
    if (!ids.length) return
    ids.forEach(id => pendingOrderIds.current.add(id))
    const pendingIds = [...pendingOrderIds.current]
    const current = request.current()
    void Promise.all(pendingIds.map(id => loadStoredOrder(id))).then(changed => {
      if (!current()) return
      pendingIds.forEach(id => pendingOrderIds.current.delete(id))
      setOrders(existing => {
        const retained = existing.filter(order => !pendingIds.includes(order.id))
        return [...retained, ...changed.filter(order => ['Submitted', 'Processing'].includes(order.status))]
      })
    }).catch(caught => { if (current()) setError(readError(caught)) })
  }, [isDemo, lastEvent])

  const confirming = orders.find((order) => order.id === confirmId) ?? null

  async function processOrder() {
    if (confirmId == null) return
    setBusy(true)
    setError(null)
    try {
      await markOrderProcessed(confirmId, confirmVersion)
      request.current()
      const next = orders.filter((order) => order.id !== confirmId)
      setOrders(next)
      replaceOpenOrderCount(next.length)
      if (viewId === confirmId) setViewId(null)
      setConfirmId(null)
    } catch (caught) {
      setError(readError(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <AdminPageHero
        title="Submissions"
        summary="Form submissions from the team waiting to be processed."
      />
      {isDemo && <Alert variant="info">This queue fills from forms submitted in the live CRM.</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <section className="admin-panel">
        {loading ? <LoadingSpinner text="Loading submissions" size="3x" /> : orders.length === 0 ? (
          <p className="mb-0">No submissions are waiting to be processed.</p>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                {sortHeader('placedAt', 'Submitted')}
                {sortHeader('customerName', 'Customer')}
                {sortHeader('formName', 'Form')}
                <th>Submitted by</th>
                {sortHeader('status', 'Status')}
                <th><span className="visually-hidden">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map((order) => (
                <tr key={order.id}>
                  <td>{new Date(order.placedAt).toLocaleString()}</td>
                  <td>{order.customerId ? <Link to={`/customers/${order.customerId}`}>{order.customerName || 'Customer'}</Link> : order.customerName || 'Customer'}</td>
                  <td>{order.formName || 'Submission'}</td>
                  <td>{order.placedByName || 'Unknown'}</td>
                  <td><Badge bg={order.status === 'Processing' ? 'primary' : 'warning'}>{orderStatusLabel(order)}</Badge></td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-primary" className="me-2" onClick={() => setViewId(order.id)}>View / edit</Button>
                    <Button size="sm" onClick={() => confirmOrder(order.id)}>Mark processed</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>
      <StoredOrderDialog
        orderId={viewId}
        onChanged={() => void refresh()}
        onHide={() => { setViewId(null); setCopied(false) }}
        footer={
          <>
            <Button
              variant="outline-primary"
              onClick={() => {
                if (viewId == null) return
                void loadStoredOrder(viewId)
                  .then((order) => navigator.clipboard.writeText(orderDetailsText(order)))
                  .then(() => setCopied(true))
                  .catch((caught) => setError(readError(caught)))
              }}
            >
              {copied ? 'Copied' : 'Copy details'}
            </Button>
            <Button onClick={() => viewId != null && confirmOrder(viewId)}>Mark processed</Button>
          </>
        }
      />
      <Modal show={confirmId != null} onHide={() => { if (!busy) setConfirmId(null) }} backdrop={busy ? 'static' : true} keyboard={!busy}>
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Mark submission processed?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {confirming && confirming.dateModified !== confirmVersion && <Alert variant="warning">This submission changed. Close this confirmation and review the revised details first.</Alert>}
          {confirming?.customerName || 'This submission'} leaves the queue. The saved form stays on the customer record.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmId(null)} disabled={busy}>Cancel</Button>
          <Button onClick={() => void processOrder()} aria-busy={busy} disabled={busy || !confirming || confirming.dateModified !== confirmVersion}>
            {busy && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}Mark processed
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
